import { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import duration from 'dayjs/plugin/duration'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import {
  deleteForm,
  fetchForm,
  fetchSubmissions,
  publishForm,
  setForm as setGlobalForm,
  setPayload,
  setThumbnails
} from '../store/form.slice'
import { getObject } from '../services/aws.service'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { CalendarGraph } from '../components/graphs/calendar-graph'
import { BarGraph } from '../components/graphs/bar-graph'
import { LineGraph } from '../components/graphs/line-graph'
import { PieGraph } from '../components/graphs/pie-graph'
import {
  capitalize,
  getDirtySubmissionForm,
  isFormDirty,
  manageSet,
  parseDate,
  sortByDateQuestion,
  sortByDateSubmitted
} from '../utils/functions'
import { FormSchema, QuestionSchema } from '../utils/types/form'
import { SubmissionSchema } from '../utils/types/submission'
import palettesData from '../data/palettes.json'

const BASE_URL = process.env.REACT_APP_BASE_URL

dayjs.extend(duration)
dayjs.extend(relativeTime)

export const FormView = () => {
  const [submissions, setSubmissions] = useState<SubmissionSchema[]>([])
  const [sortedSubmissions, setSortedSubmissions] = useState<SubmissionSchema[]>([])
  const [isDatesContextOpen, setIsDatesContextOpen] = useState(false)
  const [plotNumQuestions, setPlotNumQuestions] = useState<QuestionSchema[]>([])
  const [plotDateQuestions, setPlotDateQuestions] = useState<QuestionSchema[]>([])
  const [plotCatQuestions, setPlotCatQuestions] = useState<QuestionSchema[]>([])
  const [allQuestions, setAllQuestions] = useState<QuestionSchema[]>([])
  const [infoQuestions, setInfoQuestions] = useState<QuestionSchema[]>([])
  const [dateQuestion, setDateQuestion] = useState<QuestionSchema | null>(null)
  const [isFormOptionsOpen, setIsFormOptionsOpen] = useState(false)
  const [isSecondaryContextMenuOpen, setIsSecondaryContextMenuOpen] = useState(false)
  const [isFilterOptionsOpen, setIsFilterOptionsOpen] = useState(false)
  const [filterOptions, setFilterOptions] = useState<Map<string, Set<string>>>(new Map())
  const [isFilterOptionsAllSelected, setIsFilterOptionsAllSelected] = useState(false)
  const [isLoading, setIsLoading] = useState({
    form: false,
    submissions: false
  })
  const [requestState, setRequestState] = useState({
    info: '',
    success: '',
    error: ''
  })
  const formOptionsTriggerRef = useRef(null)
  const formOptionsContextRef = useRef(null)
  const filterOptionsTriggerRef = useRef(null)
  const filterOptionsContextRef = useRef(null)

  useOutsideClick({ element: formOptionsContextRef, trigger: formOptionsTriggerRef }, () =>
    setIsFormOptionsOpen(false)
  )

  useOutsideClick({ element: filterOptionsContextRef, trigger: filterOptionsTriggerRef }, () =>
    setIsFilterOptionsOpen(false)
  )

  const dispatch = useAppDispatch()
  const formState = useAppSelector((state) => state.form)
  const { data: globalForms, form, thumbnails } = formState
  const submissionState = useAppSelector((state) => state.submission)
  const { data: globalSubmissions } = submissionState
  const navigate = useNavigate()
  const { formId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedGraph = searchParams.get('graph')

  const theme = palettesData.find(({ name }) => name === form?.meta.colour)

  const viewTabs = [
    {
      name: 'calendar',
      element: (
        <CalendarGraph
          form={form}
          submissions={sortedSubmissions}
          questions={{ allQuestions, infoQuestions }}
          plotQuestions={{
            plotNumQuestions,
            plotDateQuestions,
            plotCatQuestions
          }}
          filters={{
            filterOptions,
            isFilterOptionsOpen,
            setIsFilterOptionsOpen,
            filterOptionsTriggerRef,
            filterOptionsContextRef,
            handleFilterOptionsChange,
            getIsFilterOptionsAllSelected,
            isFilterOptionsAllSelected
          }}
          theme={theme!}
        />
      )
    },
    {
      name: 'bar graph',
      element: (
        <BarGraph
          form={form}
          submissions={sortedSubmissions}
          questions={{ allQuestions, infoQuestions }}
          plotQuestions={{ plotNumQuestions, plotCatQuestions }}
          filters={{
            filterOptions,
            isFilterOptionsOpen,
            setIsFilterOptionsOpen,
            filterOptionsTriggerRef,
            filterOptionsContextRef,
            handleFilterOptionsChange,
            getIsFilterOptionsAllSelected,
            isFilterOptionsAllSelected
          }}
          theme={theme!}
        />
      )
    },
    {
      name: 'line chart',
      element: (
        <LineGraph
          form={form}
          submissions={sortedSubmissions}
          questions={{ allQuestions, infoQuestions }}
          plotQuestions={{
            plotNumQuestions,
            plotDateQuestions,
            plotCatQuestions
          }}
          filters={{
            filterOptions,
            isFilterOptionsOpen,
            setIsFilterOptionsOpen,
            filterOptionsTriggerRef,
            filterOptionsContextRef,
            handleFilterOptionsChange,
            getIsFilterOptionsAllSelected,
            isFilterOptionsAllSelected
          }}
          theme={theme!}
        />
      )
    },
    {
      name: 'pie chart',
      element: (
        <PieGraph
          form={form}
          submissions={sortedSubmissions}
          questions={{ allQuestions, infoQuestions }}
          plotQuestions={{ plotNumQuestions, plotCatQuestions }}
          filters={{
            filterOptions,
            isFilterOptionsOpen,
            setIsFilterOptionsOpen,
            filterOptionsTriggerRef,
            filterOptionsContextRef,
            handleFilterOptionsChange,
            getIsFilterOptionsAllSelected,
            isFilterOptionsAllSelected
          }}
          theme={theme!}
        />
      )
    }
  ]

  useEffect(() => {
    if (formId) {
      populateData(formId)
      populateThumbnails(formId)
    }
    // eslint-disable-next-line
  }, [formId])

  useEffect(() => {
    if (formId) {
      const formSubmissions = globalSubmissions[formId]
      formSubmissions && setSubmissions(JSON.parse(JSON.stringify(formSubmissions)))
    }
  }, [formId, globalSubmissions])

  useEffect(() => {
    if (form) {
      const questions = form.sections.flatMap(({ questions }) => questions)
      setAllQuestions(questions)

      const plotNumQuestions: QuestionSchema[] = []
      const plotDateQuestions: QuestionSchema[] = []
      const plotCatQuestions: QuestionSchema[] = []
      const infoQuestions: QuestionSchema[] = []

      questions.forEach((question) => {
        question.type === 'number' && plotNumQuestions.push(question)
        question.type === 'date' && plotDateQuestions.push(question)
        question.type === 'checkbox' && plotCatQuestions.push(question)

        if (question.type === 'dropdown') {
          plotCatQuestions.push(question)

          const isEveryOptionNumeric = question.meta?.dropdownOptions?.values.every(
            (option) => !Number.isNaN(Number(option))
          )

          isEveryOptionNumeric && plotNumQuestions.push(question)
        }

        if (question.type !== 'number') {
          infoQuestions.push(question)
        }
      })

      setPlotNumQuestions(plotNumQuestions)
      setPlotDateQuestions(plotDateQuestions)
      setPlotCatQuestions(plotCatQuestions)
      setInfoQuestions(infoQuestions)

      plotDateQuestions.length && setDateQuestion(plotDateQuestions[0])
    }
    // eslint-disable-next-line
  }, [form])

  useEffect(() => {
    let sortedSubmissions: SubmissionSchema[] = []

    if (dateQuestion) {
      sortedSubmissions = sortByDateQuestion(submissions, dateQuestion.id, 'asc')
    } else {
      sortedSubmissions = sortByDateSubmitted(submissions)
    }

    setSortedSubmissions(sortedSubmissions)
  }, [submissions, dateQuestion])

  useEffect(() => {
    if (plotCatQuestions.length) {
      setFilterOptions((filterOptions) => {
        const map = new Map(filterOptions)

        for (const question of plotCatQuestions) {
          manageFilterOptions(map, question.id)
        }

        return map
      })
    }
    // eslint-disable-next-line
  }, [plotCatQuestions])

  useEffect(() => {
    getIsFiltersOn()
    // eslint-disable-next-line
  }, [filterOptions])

  const populateData = async (formId: string) => {
    const existingForm = form?._id === formId ? form : globalForms.find(({ _id }) => _id === formId)

    try {
      if (existingForm) {
        dispatch(setGlobalForm(existingForm))
        const formSubmissions = globalSubmissions[existingForm._id]

        if (
          !existingForm.submissions.length ||
          existingForm.submissions.length === formSubmissions?.length
        ) {
          // Do nothing
        } else {
          setIsLoading({ ...isLoading, submissions: true })
          await dispatch(fetchSubmissions(existingForm._id)).unwrap()
        }
      } else {
        setIsLoading({ ...isLoading, form: true })
        const form = await dispatch(fetchForm(formId)).unwrap()

        if (form.submissions.length) {
          setIsLoading({ ...isLoading, submissions: true })
          await dispatch(fetchSubmissions(formId)).unwrap()
        }
      }
    } catch (error: any) {
      const errorObj = JSON.parse(error.message)
      if (errorObj.status === 403 || errorObj.status === 404) {
        setRequestState({ ...requestState, error: 'no_access' })
      } else {
        setRequestState({
          ...requestState,
          error: 'Error encountered processing the request'
        })
      }
    } finally {
      setIsLoading({ form: false, submissions: false })
    }
  }

  const populateThumbnails = async (formId: string) => {
    try {
      if (!thumbnails[formId]) {
        const url = await getObject(formId)
        dispatch(setThumbnails({ [formId]: url }))
      }
    } catch (error: any) {
      setRequestState({ ...requestState, error: error.message })
    }
  }

  const handlePublishForm = async (isPublished: boolean) => {
    if (formId) {
      try {
        await dispatch(publishForm({ id: formId, isPublished })).unwrap()
      } catch (error: any) {
        setRequestState({ ...requestState, error: error.message })
      }
    }
  }

  const handleViewForm = () => navigate(`/form/${formId}/preview`, { viewTransition: true })

  const handleEditForm = () => {
    if (form) {
      if (form.submissions.length) {
        setRequestState({
          ...requestState,
          info: `Cannot edit if ${form.isPublic ? 'responses' : 'entries'} are already submitted`
        })
      } else if (isFormDirty()) {
        setRequestState({
          ...requestState,
          info: 'Cannot edit form while another form is being drafted'
        })
      } else {
        dispatch(setPayload({ form }))
        navigate(`/form/${form._id}/edit`, { viewTransition: true })
      }
    }
  }

  const handleEditFormTheme = (name: string) => {
    if (form) {
      dispatch(
        setPayload({
          form: {
            ...form,
            meta: { ...form.meta, colour: name }
          }
        })
      )

      navigate(`/form/${form._id}/preview?edit=1&theme=1`, {
        viewTransition: true
      })
    }
  }

  const handleDeleteForm = async () => {
    if (window.confirm('Confirm delete?')) {
      requestState.error && setRequestState({ ...requestState, error: '' })

      try {
        if (form) {
          await dispatch(deleteForm(form._id)).unwrap()
          navigate('/dashboard', { viewTransition: true })
        }
      } catch (error: any) {
        setRequestState({ ...requestState, error: error.message })
      }
    }
  }

  const handleViewHistory = () => {
    if (form) {
      navigate(`/form/${form._id}/analysis?tab=${form.isPublic ? 'responses' : 'entries'}`, {
        viewTransition: true
      })
    }
  }

  const handleShare = async (form: FormSchema) => {
    const link = `${BASE_URL}/form/${form._id}/submit`
    /* const message = `Check out my online survey "${form.name}" at ${link}` */

    try {
      await navigator.clipboard.writeText(link)
      setRequestState({ ...requestState, success: 'Copied to clipboard' })
    } catch (error) {
      setRequestState({ ...requestState, error: 'Unable to copy to clipboard' })
    }
  }

  const handleAddSubmission = () => {
    if (form) {
      const dirtySubmissionForm = getDirtySubmissionForm()
      const isSubmittingForm = dirtySubmissionForm?._id === form._id

      if (dirtySubmissionForm && !isSubmittingForm) {
        setRequestState({
          ...requestState,
          info: 'Submission already in progress!'
        })
        return
      }

      navigate(`/form/${form._id}/submit`, { viewTransition: true })
    }
  }

  const getReadTime = (): string => {
    const questionsPerMinute = 2.5
    const time = allQuestions.length / questionsPerMinute
    return dayjs.duration(time, 'minutes').humanize()
  }

  const getRelativeTime = (time: number) => dayjs().to(dayjs(time))

  const manageFilterOptions = (map: Map<string, Set<string>>, questionId: string) => {
    /* Check / uncheck all */
    const iCatOptionsAllSelected = getIsFilterOptionsAllSelected(questionId)
    const catQuestion = plotCatQuestions.find(({ id }) => id === questionId)

    if (catQuestion) {
      if (iCatOptionsAllSelected) {
        if (catQuestion.type === 'dropdown') {
          map.set(questionId, new Set<string>())
        }

        if (catQuestion.type === 'checkbox') {
          map.set(questionId, new Set<string>())
        }
      } else {
        if (catQuestion.type === 'dropdown') {
          const optionSet = new Set<string>()
          catQuestion.meta?.dropdownOptions?.values.forEach((option) => optionSet.add(option))
          map.set(questionId, optionSet)
        }

        if (catQuestion.type === 'checkbox') {
          const optionSet = new Set<string>()
          catQuestion.meta?.checkboxOptions?.values.forEach((option) => optionSet.add(option))
          map.set(questionId, optionSet)
        }
      }
    }
  }

  function getIsFilterOptionsAllSelected(questionId: string): boolean {
    const catQuestion = plotCatQuestions.find(({ id }) => id === questionId)
    const optionSet = filterOptions.get(questionId)

    if (catQuestion && optionSet) {
      if (catQuestion.type === 'dropdown') {
        return catQuestion.meta?.dropdownOptions?.values.length === optionSet.size
      }

      if (catQuestion.type === 'checkbox') {
        return catQuestion.meta?.checkboxOptions?.values.length === optionSet.size
      }
    }

    return false
  }

  function getIsFiltersOn() {
    let isAllFiltersApplied = true

    for (const question of plotCatQuestions) {
      const isFilterOptionsAllSelected = getIsFilterOptionsAllSelected(question.id)

      if (!isFilterOptionsAllSelected) {
        isAllFiltersApplied = false
        break
      }
    }

    setIsFilterOptionsAllSelected(isAllFiltersApplied)
  }

  function handleFilterOptionsChange(questionId: string, option: string) {
    setFilterOptions((filterOptions) => {
      const map = new Map(filterOptions)

      if (option) {
        const optionSet = map.get(questionId)
        optionSet
          ? map.set(questionId, manageSet(optionSet, option))
          : map.set(questionId, new Set<string>().add(option))
      } else {
        manageFilterOptions(map, questionId)
      }

      return map
    })
  }

  return (
    <>
      {isLoading.form ? (
        <Spinner size='center' />
      ) : (
        <div className='main_content form_view_content'>
          <NavBack />
          {requestState.info && (
            <Status
              info={requestState.info}
              reset={() => setRequestState({ ...requestState, info: '' })}
            />
          )}
          {requestState.success && (
            <Status
              success={requestState.success}
              reset={() => setRequestState({ ...requestState, success: '' })}
            />
          )}
          {requestState.error === 'no_access' ? (
            <Status warning='Form cannot be accessed' />
          ) : (
            <>
              {form && (
                <>
                  <Status
                    error={requestState.error}
                    reset={() => setRequestState({ ...requestState, error: '' })}
                  />
                  <header>
                    <h1 className='heading-styled'>{form.name}</h1>
                    <p>{form.description}</p>
                    <div className='options'>
                      <span
                        id='options_trigger'
                        className='material-symbols-rounded options_trigger'
                        onClick={() => setIsFormOptionsOpen(!isFormOptionsOpen)}
                        ref={formOptionsTriggerRef}>
                        more_vert
                      </span>
                      {isFormOptionsOpen && (
                        <div
                          className='context_menu context_menu-right context_menu-wrap context_menu-split'
                          ref={formOptionsContextRef}>
                          {isSecondaryContextMenuOpen && (
                            <>
                              <div className='context_menu-sec'>
                                <h4>
                                  <span>Select new theme:</span>
                                </h4>
                                <div className='palette-grid'>
                                  {palettesData.map(({ name, hex }) => (
                                    <div
                                      style={{ backgroundColor: `${hex}` }}
                                      key={name}
                                      onClick={() => handleEditFormTheme(name)}
                                      title={capitalize(name)}>
                                      {form.meta.colour === name && (
                                        <span className='material-symbols-rounded'>check</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <hr />
                            </>
                          )}
                          <div>
                            <div
                              className='div-with-icon highlight-styled-hover'
                              onClick={handleEditForm}>
                              <span className='material-symbols-rounded'>edit</span>
                              <span>Edit form</span>
                            </div>
                            <hr />
                            <div
                              className={`div-with-icon ${isSecondaryContextMenuOpen ? 'highlight-styled' : ''} highlight-styled-hover`}
                              onClick={() =>
                                setIsSecondaryContextMenuOpen(!isSecondaryContextMenuOpen)
                              }>
                              <span className='material-symbols-rounded'>menu_open</span>
                              <span>Themes</span>
                            </div>
                            <hr />
                            <div className='div-with-icon error' onClick={handleDeleteForm}>
                              <span className='material-symbols-rounded'>delete</span>
                              <span>Delete form</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </header>
                  <div className='form-preview'>
                    <div>
                      <div className='card_container' onClick={handleViewForm} title='View form'>
                        {!thumbnails[form._id] ? (
                          <Spinner />
                        ) : (
                          <img src={thumbnails[form._id]} alt={form.name} />
                        )}
                      </div>
                    </div>
                    <div className='form-details'>
                      <div>
                        <span>Created on:</span>
                        <span
                          className='material-symbols-rounded'
                          onMouseEnter={() => setIsDatesContextOpen(true)}
                          onMouseLeave={() => setIsDatesContextOpen(false)}>
                          history_2
                        </span>
                        {isDatesContextOpen && (
                          <div className='context_menu context_menu-right form-details'>
                            <>
                              <span>Last modified:</span>
                              {form.updatedOn ? (
                                <span title={parseDate(form.updatedOn, 'long_date_time')}>
                                  {getRelativeTime(form.updatedOn)}
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </>
                            {/* <span>Last entry:</span>
                            <span>--</span> */}
                          </div>
                        )}
                      </div>
                      <span title={parseDate(form.createdOn, 'long_date_time')}>
                        {parseDate(form.createdOn, 'long_date')}
                      </span>
                      <span>Type:</span>
                      <div>
                        <span>{form.isPublic ? 'Public' : 'Private'}</span>
                        {form.isPublic && (
                          <div>
                            <span
                              className={form.publishedOn ? 'badge-green-icon' : 'badge-grey-icon'}>
                              {' ● '}
                            </span>
                            <span
                              className={
                                'badge ' + (form.publishedOn ? 'badge-green' : 'badge-grey')
                              }>
                              {form.publishedOn ? 'Live' : 'Offline'}
                            </span>
                          </div>
                        )}
                      </div>
                      <span className='span-a' onClick={handleViewHistory}>
                        {form.isPublic ? 'Responses' : 'Entries'}:
                      </span>
                      <span>{form.submissions.length || '--'}</span>
                      <span>Read time:</span>
                      <span>{getReadTime()}</span>
                      {form.isPublic && (
                        <>
                          <span>Share link:</span>
                          <div
                            className={`highlight highlight-link highlight-${form.publishedOn ? '' : 'blue'}`}
                            onClick={() => handleShare(form)}
                            title='Click to copy link'>
                            <span
                              className={`highlight-${form.publishedOn ? 'styled' : ''}`}>{`${BASE_URL}/form/${form._id}/submit`}</span>
                            <span className='material-symbols-rounded'>content_copy</span>
                          </div>
                        </>
                      )}
                    </div>
                    {form.isPublic && (
                      <div className='checkbox_container checkbox_container-alt'>
                        <div>
                          <input
                            id='publish'
                            type='checkbox'
                            checked={!!form.publishedOn}
                            onChange={(e) => {
                              if (!e.target.checked && !window.confirm('Unpublish form?')) {
                                return
                              }
                              handlePublishForm(e.target.checked)
                            }}
                            hidden
                          />
                          <span
                            className={`material-symbols-rounded ${form.publishedOn ? 'on' : ''}`}
                            onClick={() => {
                              if (form.publishedOn && !window.confirm('Unpublish form?')) {
                                return
                              }
                              handlePublishForm(!form.publishedOn)
                            }}>
                            {form.publishedOn ? 'toggle_on' : 'toggle_off'}
                          </span>
                          <label htmlFor='publish'>
                            {form.publishedOn ? 'Published' : 'Publish'}
                          </label>
                        </div>
                        {form.publishedOn && (
                          <span
                            className='subtext subtext-thin'
                            title={parseDate(form.publishedOn, 'long_date_time')}>
                            {`${getRelativeTime(form.publishedOn)}`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {isLoading.submissions ? (
                    <span>...</span>
                  ) : (
                    <>
                      {!!submissions.length ? (
                        <>
                          <div className={`actions_series theme-content-${form.meta.colour}`}>
                            {viewTabs.map(({ name }) => {
                              if (form.isPublic && name === 'line chart') {
                                return <Fragment key={name} />
                              }

                              return (
                                <span
                                  className={`${selectedGraph === name ? 'actions-selected' : ''}`}
                                  onClick={() => setSearchParams({ graph: name })}
                                  key={name}>
                                  {capitalize(name)}
                                </span>
                              )
                            })}
                            <div>
                              <em className='badge-orange-icon'>{' ● '}</em>
                              <em className='badge badge-orange'>More coming soon</em>
                            </div>
                          </div>
                          {viewTabs
                            .filter(({ name }) => selectedGraph === name)
                            .map(({ name, element }) => {
                              if (form.isPublic && name === 'line chart') {
                                return <Fragment key={name} />
                              }

                              return <Fragment key={name}>{element}</Fragment>
                            })}
                        </>
                      ) : (
                        <div className='no_results'>
                          {form.isPublic ? (
                            'Your form has not received any responses. Check again later.'
                          ) : (
                            <span>
                              <em>Quick tip: </em>
                              <em className='span-a' onClick={handleAddSubmission}>
                                Add your first entry
                              </em>{' '}
                              to see your graphs.
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {!form.isPublic && (
                    <div
                      className={`footer_actions footer-sticky theme-content-${form.meta.colour}`}>
                      <span
                        className='material-symbols-rounded badge-rounded'
                        onClick={handleAddSubmission}
                        title='Add new entry'>
                        add
                      </span>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  )
}
